// Subtitle copy for the Variations & SOWs tab's summary cards. The
// brief §6 spells out four scenarios — zero SOWs and zero variations,
// SOWs only, variations only, all completed — and each summary card's
// subtitle reflects the scenario it sits in. Pulling the strings out
// into a pure helper makes the four-way switch testable without
// dragging the whole orchestrator + React Query stack into a unit
// test.
//
// Active SOWs card — sits to the right of Total contract value:
//   * zero/zero          → "no SOWs yet"
//   * sows only / mixed  → "of {N} total · {M} completed"
//   * variations only    → "no SOWs commissioned yet"
//   * all completed      → "all SOWs completed"
//
// Pending variations card — rightmost on the strip:
//   * zero pending          → "no variations awaiting decision"
//   * pending exists        → "{number} · awaiting decision for {N} day(s)"
//   * variations-only mixed → covered by the pending-exists branch above
//   * variations table empty (no variations at all) → "no variations on
//     this contract" — surfaced when active SOWs > 0 but variations is
//     empty, distinguishing "no variations recorded" from "all settled".

import type { Variation } from "../../../../types";

export interface ActiveSowsSubtitleInput {
  totalSowCount: number;
  completedSowCount: number;
  /** Active variations count — used to disambiguate the variations-only
   *  scenario where an SOW count of zero deserves a different subtitle. */
  hasAnyVariations: boolean;
}

export function activeSowsSubtitle({
  totalSowCount,
  completedSowCount,
  hasAnyVariations,
}: ActiveSowsSubtitleInput): string {
  if (totalSowCount === 0) {
    if (hasAnyVariations) return "no SOWs commissioned yet";
    return "no SOWs yet";
  }
  if (totalSowCount > 0 && completedSowCount === totalSowCount) {
    return "all SOWs completed";
  }
  return `of ${totalSowCount} total · ${completedSowCount} completed`;
}

export interface PendingVariationsSubtitleInput {
  proposedVariations: readonly Variation[];
  /** Total non-archived variations on the contract — used to swap copy
   *  when the variation register is entirely empty (nothing to settle
   *  vs nothing pending). */
  totalVariationsCount: number;
  /** Today's UTC date — caller can override for deterministic tests. */
  today?: Date;
}

export function pendingVariationsSubtitle({
  proposedVariations,
  totalVariationsCount,
  today = new Date(),
}: PendingVariationsSubtitleInput): string {
  if (proposedVariations.length === 0) {
    if (totalVariationsCount === 0) return "no variations on this contract";
    return "no variations awaiting decision";
  }
  // Oldest pending first — surface the most-slipping decision.
  const sorted = [...proposedVariations].sort((a, b) =>
    a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0,
  );
  const oldest = sorted[0];
  // Day arithmetic in ISO UTC midnights, matching the table-cell
  // formatter (variationDateFormat.daysBetween). Comparing today's
  // wall-clock time against the proposed-midnight produces a
  // half-day-rounding mismatch where the summary card says "3 days"
  // while the cell says "2d ago" for the same datapoint.
  const proposedDay = oldest.createdAt.slice(0, 10);
  const [py, pm, pd] = proposedDay.split("-").map(Number);
  const proposedUtcMs = Date.UTC(py, pm - 1, pd);
  const todayUtcMs = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const days = Math.max(
    0,
    Math.round((todayUtcMs - proposedUtcMs) / 86_400_000),
  );
  const dayLabel = days === 1 ? "1 day" : `${days} days`;
  return `${oldest.number} · awaiting decision for ${dayLabel}`;
}
